import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { parseUsdInput } from "../../shared/money";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { api, ApiError, type Watch } from "../lib/api";
import { useMoney } from "../lib/CurrencyProvider";

/**
 * "Avisame": back-in-stock alert when the product is sold out, price-drop alert (optional target
 * price) while it is in stock. Emails come once; alerts are listed in Mi cuenta.
 */
export function WatchButton({ product }: { product: { id: number; slug: string; stock: number; price: number } }) {
  const { t } = useI18n();
  const { format } = useMoney();
  const { user } = useAuth();
  const kind: "stock" | "price" = product.stock > 0 ? "price" : "stock";
  const [watch, setWatch] = useState<Watch | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [target, setTarget] = useState("");
  const [error, setError] = useState<MessageKey | null>(null);

  useEffect(() => {
    if (!user) return;
    const ctrl = new AbortController();
    api
      .watches(ctrl.signal)
      .then((list) => setWatch(list.find((w) => w.product_id === product.id && w.kind === kind) ?? null))
      .catch(() => {});
    return () => ctrl.abort();
  }, [user, product.id, kind]);

  if (!user) {
    return (
      <Link className="btn btn-link watch-btn" rel="nofollow" to={`/ingresar?next=${encodeURIComponent(`/producto/${product.slug}`)}`}>
        {t(kind === "stock" ? "watch.stock" : "watch.price")}
      </Link>
    );
  }

  const create = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    const cents = kind === "price" ? parseUsdInput(target) : null;
    if (Number.isNaN(cents)) return setError("watch.errors.target");
    try {
      const w = await api.createWatch(product.id, kind, cents);
      setWatch({ id: w.id, kind, product_id: product.id, target_price: cents } as Watch);
      setOpenForm(false);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? "watch.errors.target" : "auth.errors.generic");
    }
  };

  const cancel = async () => {
    if (!watch) return;
    await api.deleteWatch(watch.id).catch(() => {});
    setWatch(null);
  };

  if (watch) {
    return (
      <p className="watch-on" role="status">
        {kind === "stock" ? t("watch.stockOn") : watch.target_price ? t("watch.priceOnTarget", { price: format(watch.target_price) }) : t("watch.priceOn")}{" "}
        <button type="button" className="btn btn-link btn-sm" onClick={cancel}>
          {t("watch.cancel")}
        </button>
      </p>
    );
  }

  if (kind === "stock") {
    return (
      <>
        <button type="button" className="btn btn-outline watch-btn" onClick={() => create()}>
          {t("watch.stock")}
        </button>
        {error && <p className="field-error">{t(error)}</p>}
      </>
    );
  }

  return openForm ? (
    <form className="watch-form" onSubmit={create}>
      <label htmlFor="watch-target">{t("watch.targetLabel")}</label>
      <input id="watch-target" inputMode="decimal" placeholder={t("watch.targetPlaceholder")} value={target} onChange={(e) => setTarget(e.target.value)} />
      <button type="submit" className="btn btn-outline btn-sm">
        {t("watch.create")}
      </button>
      {error && <p className="field-error">{t(error)}</p>}
    </form>
  ) : (
    <button type="button" className="btn btn-link watch-btn" onClick={() => setOpenForm(true)}>
      {t("watch.price")}
    </button>
  );
}
